import { useCallback, useState } from "react";
import {
  createPromoCodeBatchRequest,
  deletePromoCodeBatchRequest,
  exportPromoCodeBatchCsvRequest,
  getPromoCodeBatchDetailRequest,
  listPromoCodeBatchesRequest,
} from "@services/promoCodeBatches/promoCodeBatchesApi";

const initialPagination = { page: 1, limit: 20, total: 0, totalPages: 1 };

function usePromoCodeBatches() {
  const [batches, setBatches] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const fetchBatches = useCallback(async (params = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await listPromoCodeBatchesRequest(params);
      setBatches(response.body.batches);
      setPagination(response.body.pagination);
    } catch (requestError) {
      setError(requestError.message);
      setBatches([]);
      setPagination(initialPagination);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBatchDetail = useCallback(async (id, params = {}) => {
    if (!id) return null;
    setDetailLoading(true);
    setError("");
    try {
      const response = await getPromoCodeBatchDetailRequest(id, params);
      setSelectedBatch(response.body);
      return response.body;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const createBatch = useCallback(async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      const response = await createPromoCodeBatchRequest(payload);
      setSelectedBatch(response.body);
      return response.body;
    } catch (requestError) {
      setError(requestError.message);
      throw requestError;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const exportBatch = useCallback(async (id, status = "all") => {
    if (!id) return;
    setExporting(true);
    setError("");
    try {
      await exportPromoCodeBatchCsvRequest(id, status);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setExporting(false);
    }
  }, []);

  const deleteBatch = useCallback(async (id) => {
    if (!id) return null;
    setDeleting(true);
    setError("");
    try {
      const response = await deletePromoCodeBatchRequest(id);
      setSelectedBatch(null);
      return response?.body || null;
    } catch (requestError) {
      setError(requestError.message);
      throw requestError;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    batches,
    pagination,
    selectedBatch,
    loading,
    detailLoading,
    submitting,
    exporting,
    deleting,
    error,
    fetchBatches,
    fetchBatchDetail,
    createBatch,
    exportBatch,
    deleteBatch,
    clearSelectedBatch: () => setSelectedBatch(null),
  };
}

export default usePromoCodeBatches;
